import React, { useMemo } from "react";
import { LabelBadge } from "../../../common/badges/label-badge";
import { EstimateBadge } from "../../../common/badges/estimate-badge";
import { PriorityBadge } from "../../../common/badges/priority-badge";
import { StatusBadge } from "../../../common/badges/status-badge";
import AssigneeDropdown from "../../../common/issues/assignee-dropdown";
import { ShortcutProviderRoot } from "../../../providers/ShortcutProvider";
import { User } from "@/types/users";
import { getColorValue } from "@/utils/labelUtils";
import { Issue } from "@/types/issues";
import { Button } from "@/components/ui/button";
import { useCanvasStatusManagement } from "@/components/ProjectConfiguration/hooks";
import { LabelInterface } from "@/types/labels";
import { resolveIssueStatus } from "@/utils/filterUtils";

export interface PRDProject {
  id: string;
  repoPath?: string;
  provider?: string;
  labels?: LabelInterface[];
  users?: User[];
}

interface PRDIssueData {
  labelInterfaces: LabelInterface[];
  projectLabels: LabelInterface[];
}

interface ProjectUsersMap {
  [projectId: string]: User[];
}

interface PRDPropertiesProps {
  project?: PRDProject;
  issue?: Issue;
  issueData: PRDIssueData;
  usersForProject: ProjectUsersMap;
}

const PRDProperties: React.FC<PRDPropertiesProps> = ({
  project,
  issue,
  issueData,
  usersForProject,
}) => {
  const { comprehensiveStatusList } = useCanvasStatusManagement();
  const allLabels = issueData.labelInterfaces;

  const status = useMemo(() => {
    const availableStatusNames =
      comprehensiveStatusList?.map((status) => status.name) || [];
    const resolvedStatus = resolveIssueStatus(
      issue?.featurePrd?.status,
      availableStatusNames,
    );

    return comprehensiveStatusList?.find((s) => s.name === resolvedStatus);
  }, [comprehensiveStatusList, issue?.featurePrd?.status]);

  return (
    <div className="w-60 min-h-96 border-l border-border-primary bg-secondary-n-1000 overflow-y-auto">
      <ShortcutProviderRoot>
        <div className="p-4">
          <Button
            variant="primary"
            size="sm"
            className="w-full py-4"
            onClick={() => {
              if (project && issue?.canvas) {
                const baseURL = window.location.origin;
                window.open(
                  `${baseURL}/canvases/${issue?.canvas.id}/editor`,
                  "_blank",
                );
              }
            }}
            data-testid="open-canvas-button"
          >
            Open Canvas
          </Button>
        </div>
        <div className="px-4 pb-4 pt-2 space-y-4">
          <div>
            <h3 className="text-2xs font-medium text-text-secondary mb-2">
              Properties
            </h3>
            <div className="flex flex-col gap-2 items-start">
              {project && issue?.canvas && (
                <AssigneeDropdown
                  className="h-7! bg-transparent text-text-primary! text-xs font-medium!"
                  issueId={issue?.canvas?.id ?? ""}
                  users={usersForProject[project.id] || []}
                  value={issue?.canvas?.assigned_user_id ?? null}
                  variant="text-dark"
                  borderless={true}
                />
              )}

              {issue?.canvas && comprehensiveStatusList && (
                <StatusBadge
                  issueId={issue?.canvas?.id ?? ""}
                  status={status || null}
                  statuses={comprehensiveStatusList}
                  variant="text-dark"
                  borderless={true}
                />
              )}

              {issue?.canvas && (
                <PriorityBadge
                  issueId={issue?.canvas?.id}
                  priority={issue?.featurePrd?.priority ?? null}
                  variant="text-dark"
                  borderless={true}
                />
              )}

              {issue?.canvas && (
                <EstimateBadge
                  issueId={issue?.canvas?.id}
                  estimate={issue?.featurePrd?.estimate ?? null}
                  variant="text-dark"
                  borderless={true}
                  iconColor="var(--secondary-n-600)"
                />
              )}
            </div>
          </div>
          <div>
            <h3 className="text-2xs font-medium text-text-secondary mb-2.5">
              Labels
            </h3>
            <div className="flex flex-wrap items-center gap-2 w-full max-w-full overflow-hidden">
              {allLabels && allLabels.length > 0 ? (
                <>
                  {allLabels.map((label: LabelInterface) => (
                    <div
                      key={label.id}
                      className="flex items-center gap-2 px-1.5 py-1 rounded-md text-2xs text-text-primary font-medium border border-border-primary max-w-48"
                    >
                      <div
                        className="w-3 h-3 rounded-sm shrink-0"
                        style={{
                          backgroundColor: getColorValue(label.color),
                        }}
                      />
                      <span className="truncate">
                        {label.name || label.label}
                      </span>
                    </div>
                  ))}
                  <LabelBadge
                    className="h-7! w-7! p-0! bg-transparent border-none hover:bg-secondary-n-800 text-text-primary"
                    issue={issue}
                    projectLabels={project?.labels as any}
                    labelsToStack={allLabels}
                    showPlusButton
                  />
                </>
              ) : (
                <>
                  <LabelBadge
                    className="text-2xs px-1 py-1 h-auto text-secondary-n-400 border border-secondary-n-900 bg-transparent"
                    title="Add Label"
                    iconColor="var(--icons-secondary)"
                    issue={issue as any}
                    projectLabels={project?.labels as any}
                    labelsToStack={allLabels}
                    iconSize={12}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </ShortcutProviderRoot>
    </div>
  );
};

export default PRDProperties;
